import { useState, useEffect, useMemo } from "react";
import { getCountryCodeFromCoordinates, setClientDomain, COUNTRY_CHANGED_EVENT } from "../../utils/Common";
import { cn } from "@/lib/utils";

/**
 * Country switcher on the auth screens, as a segmented control.
 *
 * The previous stylesheet carried a block labelled "estilos temporales" that
 * overrode the real styles with `color: rgba(245,245,245,.737)` on `#d7d7d7`
 * at 50% opacity, so the PA/US labels were effectively invisible in both
 * themes. Colours now come from tokens.
 */
const COUNTRIES = [
  { code: "pa", name: "PA", flag: "/img/pa_flag.png", actived: true },
  { code: "us", name: "US", flag: "/img/usa_flag.png", actived: true },
];

const DEFAULT_COUNTRY = "pa";

const CountryList = () => {
  const countries = useMemo(() => COUNTRIES, []);

  // A country already chosen (or already detected) is the answer: re-detecting
  // adds nothing and costs a browser permission prompt every single visit.
  const storedCountry = countries.some((c) => c.code === localStorage.getItem("country"))
    ? localStorage.getItem("country")
    : null;

  const [selectedCountry, setSelectedCountry] = useState(storedCountry || DEFAULT_COUNTRY);
  const [locating, setLocating] = useState(!storedCountry);

  useEffect(() => {
    // Only ever ask once, the first time we have nothing stored. Answering the
    // prompt (or dismissing it) writes a country, so it never fires again.
    if (storedCountry || !("geolocation" in navigator)) {
      setLocating(false);
      return;
    }

    let cancelled = false;

    const resolve = (code) => {
      if (cancelled) return;
      localStorage.setItem("country", code);
      setSelectedCountry(code);
      setLocating(false);
    };

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const detected = getCountryCodeFromCoordinates(coords.latitude, coords.longitude);
        const match = countries.find((country) => country.code === detected);
        resolve(match?.code || DEFAULT_COUNTRY);
      },
      () => resolve(DEFAULT_COUNTRY),
      { timeout: 8000, maximumAge: Infinity }
    );

    return () => { cancelled = true; };
  }, [countries, storedCountry]);

  // Manejar clic en un país
  const handleCountryClick = (country) => {
    if (!country.actived || country.code === selectedCountry) return;

    localStorage.setItem("country", country.code);
    setSelectedCountry(country.code);
    // Rewrites the API base in sessionStorage. getClientDomain() reads it on
    // every call, so the next request already goes to the new country: the
    // full page reload this used to do was never necessary here. The country
    // picker only exists on the auth screens, where nothing has been fetched
    // yet, so there is no stale data to clear either.
    setClientDomain(window.location.hostname);
    window.dispatchEvent(new Event(COUNTRY_CHANGED_EVENT));
  };

  // Never a full-screen overlay: this control is one small element on the form,
  // and covering the whole page while the geolocation prompt waits for an answer
  // is what put a grey sheet over the login.
  if (locating) {
    return (
      <div
        aria-hidden="true"
        className="h-[38px] w-[132px] animate-pulse rounded-full bg-muted"
      />
    );
  }

  return (
    <div
      role="group"
      aria-label="País"
      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted p-1"
    >
      {countries.map((country) => {
        const isSelected = selectedCountry === country.code;
        return (
          <button
            key={country.code}
            type="button"
            disabled={!country.actived}
            aria-pressed={isSelected}
            onClick={() => handleCountryClick(country)}
            className={cn(
              "flex items-center gap-2 rounded-full border-0 px-3 py-1.5",
              "text-xs font-semibold transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isSelected
                ? "bg-primary text-primary-foreground shadow-sm"
                : "bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              !country.actived && "cursor-not-allowed opacity-50"
            )}
          >
            <img
              src={country.flag}
              alt=""
              aria-hidden="true"
              className="size-5 rounded-full object-cover"
            />
            {country.name}
          </button>
        );
      })}
    </div>
  );
};

export default CountryList;
